<template>
	<div>
		<v-row v-if="!isEmpty(summary)" no-gutters class="px-1 mt-1">
			<v-col v-for="(count, type) in summary" :key="type" cols="6" sm="3" class="pa-1 ">
				<v-card class="d-flex flex-column align-center justify-center px-2 py-6 fill-height" elevation="2" outlined tile
					@click="applyFilter(type)">
					<span class="subtitle-2 mb-3">{{ summaryText[type] }}</span>
					<span class="display-3">{{ count.recent }}</span>
					<span v-if="count.older" class="caption">plus {{ count.older }} older alarms</span>
					<v-spacer v-else />
				</v-card>
			</v-col>
		</v-row>
		<v-card class="mx-2 mt-1" tile elevation="3">
			<v-row no-gutters>
				<v-col cols="12" class="d-flex align-end justify-end">
					<v-btn text color="ocean" small :disabled="loadingAlerts" @click="reset">
						<!-- <v-icon class="mr-1" small>fa-solid fa-filter-circle-xmark</v-icon> -->
						Reset filters
					</v-btn>
				</v-col>
			</v-row>
			<v-row class="d-flex d-sm-none v-data-table-header mx-0">
				<v-col cols="6">
					<v-autocomplete
						v-model="query.controllerName"
						:disabled="loadingAlerts || !query.communityIds"
						:items="sortedRooms"
						item-text="name"
						item-value="name"
						class="flex-shrink-1"
						label="Room"
						dense
						clearable
						hide-details
					></v-autocomplete>
				</v-col>
				<v-col cols="6">
					<v-autocomplete
						v-model="query.communityIds"
						:disabled="loadingAlerts"
						:items="allCommunities"
						item-text="name"
						item-value="id"
						class="flex-shrink-1"
						label="Community"
						dense
						clearable
						hide-details
					></v-autocomplete>
				</v-col>
				<v-col cols="6">
					<v-select
						v-model="query.alarmType"
						:disabled="loadingAlerts"
						:items="alarmTypes"
						class="flex-shrink-1"
						label="Alarm"
						menu-props="offsetY"
						multiple
						dense
						clearable
						hide-details
					></v-select>
				</v-col>
				<v-col cols="6">
					<v-select
						v-model="query.alarmStatus"
						:disabled="loadingAlerts"
						:items="alarmStatus"
						class="flex-shrink-1"
						label="Status"
						menu-props="offsetY"
						multiple
						dense
						hide-details
						clearable
					></v-select>
				</v-col>
				<v-col>
					<shared-date-range
						v-model="query.range"
						:disabled="loadingAlerts"
						label="Range"
						:append-icon="sortIcon"
						dense
						hide-details
						clearable
						@click:append="toggleSort(query.sort)"
					></shared-date-range>
				</v-col>
			</v-row>
			<v-data-table
				class="input-header-table"
				:class="{ 'table-loading': loadingAlerts }"
				:items="alerts"
				:headers="headers"
				:footer-props="footer"
				:loading="loadingAlerts"
				:options.sync="options"
				:server-items-length="count"
				:hide-default-header="$vuetify.breakpoint.xsOnly"
				disable-sort
				@click:row="goToItem"
			>
				<template #[`header.controller_name`]="{ header }">
					<v-autocomplete
						v-model="query.controllerName"
						:disabled="loadingAlerts || !query.communityIds"
						:items="sortedRooms"
						item-text="name"
						item-value="name"
						class="flex-shrink-1"
						:label="header.text"
						dense
						clearable
						hide-details
					></v-autocomplete>
				</template>
				<template #[`header.alarm_type`]="{ header }">
					<v-select
						v-model="query.alarmType"
						:disabled="loadingAlerts"
						:items="alarmTypes"
						class="flex-shrink-1"
						:label="header.text"
						:menu-props="{ nudgeBottom: '30px' }"
						multiple
						dense
						clearable
						hide-details
					></v-select>
				</template>
				<template #[`header.community_id`]="{ header }">
					<v-autocomplete
						v-model="query.communityIds"
						:disabled="loadingAlerts"
						:items="allCommunities"
						item-text="name"
						item-value="id"
						class="flex-shrink-1"
						:label="header.text"
						dense
						clearable
						hide-details
					></v-autocomplete>
				</template>
				<template #[`header.alarm_status`]="{ header }">
					<v-select
						v-model="query.alarmStatus"
						:disabled="loadingAlerts"
						:items="alarmStatus"
						class="flex-shrink-1"
						:label="header.text"
						:menu-props="{ nudgeBottom: '30px' }"
						multiple
						dense
						hide-details
						clearable
					></v-select>
				</template>
				<template #[`header.range`]="{ header }">
					<shared-date-range
						v-model="query.range"
						:disabled="loadingAlerts"
						:label="header.text"
						:append-icon="sortIcon"
						dense
						hide-details
						clearable
						@click:append="toggleSort(query.sort)"
					></shared-date-range>
				</template>
				<template #[`item.sys_id`]="{ item }">
					{{ roomName(item.sys_id) }}
				</template>
				<template #[`item.alarm_type`]="{ item }">
					<v-tooltip top>
						<template #activator="{ on, attrs }">
							<span v-if="$vuetify.breakpoint.xsOnly" v-bind="attrs" v-on="on" @click.prevent.stop v-text="deviceAlarmType(item)" />
							<span v-else v-bind="attrs" v-on="on" v-text="deviceAlarmType(item)" />
						</template>
						<span v-text="valueText(item)" />
					</v-tooltip>
				</template>
				<template #[`item.device`]="{ item }">
					{{ deviceAlarm(item) }}
				</template>
				<template #[`item.community_id`]="{ item }">
					{{ communityName(item.community_id) }}
				</template>
				<template #[`item.alarm_status`]="{ item }">
					<StatusSelect
						v-if="!item.alarm_status.includes('DISABLED')"
						:status="item.alarm_status"
						:severity="item.severity"
						@select="changeStatus($event, item)"
					/>
					<StatusChip
						v-else
						:status="item.alarm_status"
						:severity="item.severity"
						:action="() => enableAlarm(item)"
					/>
				</template>
				<template #[`item.severity`]="{ item }">
					{{ capitalize(item.severity) }}
				</template>
				<template #[`item.range`]="{ item }">
					{{ item.ts | parseDate }}
				</template>
			</v-data-table>
		</v-card>

		<!-- <v-alert type="info" outlined dismissible>
			Missing a room? It might not have sent an alarm yet.
		</v-alert> -->

		<v-banner v-if="pendingTitle" elevation="2" class="ma-2" icon="fa-info-circle">
			<p class="subtitle-2">{{ pendingTitle }}</p>
			<span>{{ strings.pendingDesc }}</span>
			<template #actions>
				<v-btn text color="primary" :disabled="loadingAlerts" @click="togglePending">
					{{ showPending ? strings.hide : strings.show }}
				</v-btn>
			</template>
		</v-banner>

		<v-banner v-if="olderTitle" elevation="2" class="ma-2" icon="fa-triangle-exclamation" icon-color="warning">
			<p class="subtitle-2">{{ olderTitle }}</p>
			<span>{{ strings.olderDesc }}</span>
			<template #actions>
				<v-btn text color="primary" :disabled="loadingAlerts" @click="toggleOlder">
					{{ showOlder ? strings.hide : strings.show }}
				</v-btn>
			</template>
		</v-banner>

		<comment-dialog
			ref="CommentDialog"
			max-width="600px"
			@reload="fetchAlerts"
		/>
		<dialog-confirm-dialog
			ref="ConfirmDialog"
			:title="strings.dialog.enable.title"
			:body="
				strings.dialog.enable.body(
					deviceAlarmType(currentAlarm),
					deviceAlarm(currentAlarm)
				)
			"
			:confirm="strings.dialog.enable.confirm"
		/>
	</div>
</template>

<script>
import _ from "lodash";
import { mapActions, mapGetters } from "vuex";

import { alarmStatus, alarmTypes, summaryText, summaryFilters } from "./status";
import { alarmDate } from "@/utils/dateHelpers";
import strings from "@/utils/strings";

export default {
	components: {
		StatusChip: () => import("./StatusChip.vue"),
		StatusSelect: () => import("./StatusSelect.vue"),
		CommentDialog: () => import("./dialogs/CommentDialog.vue"),
	},

	filters: {
		parseDate: alarmDate,
	},

	data() {
		return {
			strings: strings.k4noc.alarms,
			headers: [
				{ text: "Room", value: "controller_name" },
				{ text: "Alarm", value: "alarm_type" },
				{ text: "Device", value: "device" },
				{ text: "Community", value: "community_id" },
				{ text: "Status", value: "alarm_status" },
				{ text: "Range", value: "range" },
			],
			footer: {
				showCurrentPage: true,
				itemsPerPageOptions: [10, 15, 20, 50],
			},
			alarmTypes,
			alarmStatus,
			summaryText,
			query: {
				controllerName: null,
				alarmType: null,
				communityIds: null,
				alarmStatus: null,
				sort: "ASC",
				range: [],
			},
			currentAlarm: null,
		};
	},

	computed: {
		...mapGetters({
			alerts: "tickets/deviceStatus/alerts",
			summary: "tickets/deviceStatus/summary",
			loadingAlerts: "tickets/deviceStatus/loading",
			queryFilters: "tickets/deviceStatus/query",
			count: "tickets/deviceStatus/count",
			page: "tickets/deviceStatus/page",
			limit: "tickets/deviceStatus/limit",
			pending: "tickets/deviceStatus/pending",
			showPending: "tickets/deviceStatus/showPending",
			older: "tickets/deviceStatus/older",
			showOlder: "tickets/deviceStatus/showOlder",
			rooms: "rooms/items",
			communityById: "communities/itemById",
			roomById: "rooms/item",
		}),

		allCommunities() {
			let communities
			if (this.$store.getters["auth/roles"].some(role => ["Admin", "Advocate"].includes(role))) {
				communities = this.$store.getters["communities/items"];
			} else {
				communities = this.$store.getters["auth/communities"];
			}
			return _.sortBy(communities, "name");
		},

		sortedRooms() {
			return _.sortBy(this.rooms, "name");
		},

		sortIcon() {
			return "fas fa-arrow-" + (this.query.sort === "ASC" ? "up" : "down");
		},

		options: {
			get() {
				return {
					itemsPerPage: this.limit,
					page: this.page,
				};
			},
			set({ itemsPerPage, page }) {
				if (itemsPerPage !== this.limit)
					this.changeQuery({ limit: itemsPerPage });
				if (page !== this.page)
					this.changeQuery({ page: page - 1 });
			},
		},

		pendingTitle() {
			return this.pending ? `${this.pending} ${this.strings.pending} ${this.showPending ? this.strings.shown : ''}` : false;
		},
		olderTitle() {
			return this.older ? `${this.older} ${this.strings.older} ${this.showOlder ? this.strings.shown : ''}` : false;
		},
	},

	watch: {
		"query.controllerName"(controllerName) {
			this.changeQuery({ controllerName, reset: true });
		},

		"query.alarmType"(alarmType) {
			this.changeQuery({ alarmType, reset: true });
		},

		"query.communityIds"(communityIds, old) {
			this.getRooms(this.communityById(communityIds));
			if (old && this.query.controllerName) {
				this.query.controllerName = undefined;
			}
			this.changeQuery({ communityIds, reset: true });
		},

		"query.alarmStatus"(alarmStatus) {
			this.changeQuery({ alarmStatus, reset: true });
		},

		"query.range": {
			deep: true,
			handler([startDate, endDate]) {
				// if (!endDate) endDate = startDate;
				// this.changeQuery({ startDate, endDate });
				if (!startDate || endDate) this.changeQuery({ startDate, endDate, reset: true });
			},
		},
	},

	created() {
		this.initFilters();
		this.fetchAlerts();
	},

	methods: {
		...mapActions({
			loadFilters: "tickets/deviceStatus/loadFilters",
			resetFilters: "tickets/deviceStatus/resetFilters",
			fetchAlerts: "tickets/deviceStatus/debounceAlerts",
			changeQuery: "tickets/deviceStatus/changeQuery",
			getRooms: "rooms/load",
		}),
		capitalize: _.capitalize,
		startCase: _.startCase,
		isEmpty: _.isEmpty,

		communityName(id) {
			const community = this.communityById(id);
			return _.get(community, "name", id);
		},

		roomName(id) {
			const room = this.roomById(id);
			return _.get(room, "name", "");
		},

		deviceAlarm(item) {
			if (!item) return "";
			return item.path.replace("Devices/", "").replaceAll("/", " ");
		},

		deviceAlarmType(item) {
			if (!item) return "";
			const alarm = alarmTypes.find((alarm) => alarm.value === item.alarm_type);
			return _.get(alarm, "text", item.alarm_type);
		},

		valueText(item) {
			const type = alarmTypes.find((alarm) => alarm.value === item.alarm_type) || {};
			let value = String(item.value);
			value = _.isNaN(Number(value)) ? `"${_.capitalize(value)}"` : _.round(value, 2);
			return `Current value is ${value}${type.unit || ""}`;
		},

		toggleSort(sort) {
			this.query.sort = sort === "ASC" ? "DESC" : "ASC";
			this.changeQuery({ sort: this.query.sort });
		},

		togglePending() {
			this.$store.commit("tickets/deviceStatus/setShowPending", !this.showPending);
			this.fetchAlerts();
		},

		toggleOlder() {
			this.$store.commit("tickets/deviceStatus/setShowOlder", !this.showOlder);
			this.fetchAlerts();
		},

		reset() {
			this.resetFilters();
			this.initFilters();
			this.fetchAlerts();
		},

		initFilters() {
			this.loadFilters();

			const { startDate, endDate, ...query } = this.queryFilters;

			this.query = {
				...this.query,
				...query,
				range: _.filter([startDate, endDate]),
			};
		},

		changeStatus(status, ticket) {
			this.$refs.CommentDialog.pop({
				status,
				ticket,
			});
		},

		goToItem(item) {
			if (!item.sys_id) return;
			this.$router.push({ path: item.sys_id, append: true });
		},

		async enableAlarm(alarm) {
			if (!alarm.alarm_status.includes("DISABLED")) return;
			this.currentAlarm = alarm;

			const confirmed = await this.$refs.ConfirmDialog.pop();
			if (!confirmed) return;

			const fullName = this.$store.getters["auth/fullName"];
			const email = this.$store.getters["auth/email"];
			const user = fullName === " " ? email : fullName;

			try {
				await this.$store.dispatch("tickets/deviceStatus/reenable", { id: alarm.id, updatedBy: user });
			} finally {
				await this.fetchAlerts();

				this.currentAlarm = null;
			}
		},

		applyFilter(summaryType) {
			const filters = summaryFilters[summaryType];
			if (filters) {
				this.query.alarmType = filters;
				this.query.alarmStatus = ["ACTIVE", "ACKNOWLEDGED"];
				this.query.range = [];
				this.query.controllerName = null;
			}
		}
	},
};
</script>

<style lang="scss" scoped>
@import "./InputHeaderTable.scss";

::v-deep > .v-list--dense .v-list-item {
	height: 40px;
}
</style>
